import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import LinearProgress from '@mui/material/LinearProgress';
import Slider from '@mui/material/Slider';
import styled from '@emotion/styled';
import { IdCard, User, Mail, Cake, Users, Compass, Briefcase, Mars, Venus } from 'lucide-react';
import { assessmentPatchSession, assessmentEmailResumeLink } from 'actions/assessment';
import { getLocalizedPath } from 'helpers/getLocale';

const BODY_FONT = 'Rubik-Regular, system-ui, sans-serif';

const STORAGE_KEY = 'assessment_user_info';
const STEPS = ['intro', 'name', 'email', 'age', 'gender', 'certainty', 'occupations'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEP_ICONS = {
  intro: IdCard,
  name: User,
  email: Mail,
  age: Cake,
  gender: Users,
  certainty: Compass,
  occupations: Briefcase,
};

const IconWrap = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 80,
  height: 80,
  borderRadius: 24,
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.main,
  marginBottom: 24,
}));

const ProgressWrap = styled('div')(({ theme }) => ({
  marginBottom: 28,
}));

const ProgressLabel = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1.2,
  color: theme.palette.grey[500],
  textAlign: 'left',
  marginBottom: 10,
}));

const Bar = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const Question = styled(Typography)({
  fontSize: 22,
  fontWeight: 700,
  textAlign: 'left',
  marginBottom: 10,
  lineHeight: 1.3,
});

const Hint = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 14,
  color: theme.palette.grey[600],
  textAlign: 'left',
  marginBottom: 28,
  lineHeight: 1.5,
}));

const InputWrap = styled('div')({
  marginBottom: 24,
});

const StyledInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontFamily: BODY_FONT,
    borderRadius: 14,
    fontSize: 18,
  },
}));

const GenderCardsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 12,
  marginBottom: 16,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const GenderCard = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  flex: 1,
  fontFamily: BODY_FONT,
  fontSize: 16,
  fontWeight: 500,
  textTransform: 'none',
  padding: '32px 18px',
  borderRadius: 16,
  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid #e0e0e0',
  backgroundColor: isSelected ? theme.palette.primary.light : '#fff',
  color: isSelected ? theme.palette.primary.main : theme.palette.grey[800],
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    borderColor: isSelected ? theme.palette.primary.main : '#bdbdbd',
  },
}));

const OtherLinkWrap = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: 24,
});

const OtherLink = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  background: 'none',
  border: 'none',
  fontFamily: BODY_FONT,
  fontSize: 13,
  color: isSelected ? theme.palette.primary.main : theme.palette.grey[600],
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  cursor: 'pointer',
  padding: '4px 0',
  '&:hover': {
    color: isSelected ? theme.palette.primary.main : theme.palette.grey[800],
  },
}));

const Scale = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 10,
});

const ScaleNumber = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  flex: 1,
  height: 56,
  minWidth: 0,
  borderRadius: 16,
  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid #e0e0e0',
  backgroundColor: isSelected ? theme.palette.primary.main : '#fff',
  color: isSelected ? '#fff' : theme.palette.grey[700],
  fontFamily: BODY_FONT,
  fontSize: 18,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const CertaintySliderWrap = styled('div')({
  padding: '8px 12px 4px',
  marginBottom: 4,
});

const ScaleEnds = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontFamily: BODY_FONT,
  fontSize: 12,
  color: theme.palette.grey[500],
  marginBottom: 18,
}));

const SelectedLabel = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 14,
  color: theme.palette.primary.main,
  fontWeight: 600,
  marginBottom: 24,
  minHeight: 22,
}));

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  marginTop: 8,
});

const PrimaryButton = styled(Button)({
  fontFamily: BODY_FONT,
  borderRadius: 25,
  padding: '12px 36px',
  fontSize: 15,
  fontWeight: 600,
  textTransform: 'none',
  marginLeft: 'auto',
});

const FullPrimary = styled(Button)({
  fontFamily: BODY_FONT,
  borderRadius: 25,
  padding: '14px 32px',
  fontSize: 16,
  fontWeight: 600,
  textTransform: 'none',
  width: '100%',
  marginTop: 8,
});

const IntroHeading = styled(Typography)({
  fontSize: 26,
  fontWeight: 700,
  textAlign: 'left',
  marginBottom: 12,
  lineHeight: 1.3,
});

const IntroLede = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 15,
  color: theme.palette.grey[700],
  textAlign: 'left',
  marginBottom: 28,
  lineHeight: 1.55,
}));

const CERTAINTY_OPTIONS = [-2, -1, 0, 1, 2];

function loadDraft() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function saveDraft(values) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
}

export default function InfoWizard({ onComplete }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { step: paramStep } = useParams();
  const { t } = useTranslation();
  const [values, setValues] = useState(() => loadDraft());

  const stepKey = STEPS.includes(paramStep) ? paramStep : 'intro';
  const stepIndex = STEPS.indexOf(stepKey);
  const totalQuestions = STEPS.length - 1; // intro doesn't count
  const questionNumber = Math.max(0, stepIndex);

  useEffect(() => {
    saveDraft(values);
  }, [values]);

  const goToStep = useCallback((next) => {
    navigate(getLocalizedPath(`/assessment/info/${next}`));
  }, [navigate]);

  const goNext = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex >= STEPS.length) {
      finish();
    } else {
      goToStep(STEPS[nextIndex]);
    }
  };

  const setField = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const finish = async () => {
    const trimmedEmail = (values.email || '').trim();
    const occupationsRaw = typeof values.occupationsInMind === 'string'
      ? values.occupationsInMind.trim()
      : '';
    const userInfo = {
      name: (values.name || '').trim(),
      email: trimmedEmail || null,
      birth_year: values.birthYear || null,
      gender: values.gender || null,
      career_certainty: typeof values.certainty === 'number' ? values.certainty : null,
      occupations_in_mind: occupationsRaw || null,
    };
    try {
      await dispatch(assessmentPatchSession({ user_info: userInfo }));
    } catch (err) {
      console.error('Persist user info failed:', err);
    }
    // Keep the wizard values around (the auto-save effect already wrote them)
    // so Results.jsx can prefill the email panel and personalize the greeting.
    onComplete?.(userInfo);
  };

  const renderProgress = () => (
    <ProgressWrap>
      <ProgressLabel>
        {t('Assessment.infoStepProgress', { current: questionNumber, total: totalQuestions })}
      </ProgressLabel>
      <Bar variant="determinate" value={(questionNumber / totalQuestions) * 100} />
    </ProgressWrap>
  );

  const renderIcon = () => {
    const Icon = STEP_ICONS[stepKey];
    if (!Icon) return null;
    return (
      <IconWrap>
        <Icon size={40} strokeWidth={1.75} />
      </IconWrap>
    );
  };

  if (stepKey === 'intro') {
    return (
      <>
        {renderIcon()}
        <IntroHeading variant="h1">{t('Assessment.infoIntroHeading')}</IntroHeading>
        <IntroLede>{t('Assessment.infoIntroLede')}</IntroLede>
        <FullPrimary variant="contained" color="primary" size="large" onClick={() => goToStep('name')}>
          {t('Assessment.infoIntroBegin')}
        </FullPrimary>
      </>
    );
  }

  if (stepKey === 'name') {
    const valid = (values.name || '').trim().length > 0;
    return (
      <>
        {renderProgress()}
        <Question variant="h1">{t('Assessment.infoNameQuestion')}</Question>
        <Hint>{t('Assessment.infoNameHint')}</Hint>
        <InputWrap>
          <StyledInput
            fullWidth
            autoFocus
            value={values.name || ''}
            onChange={(e) => setField('name', e.target.value)}
            placeholder={t('Assessment.infoNamePlaceholder')}
            onKeyDown={(e) => { if (e.key === 'Enter' && valid) goNext(); }}
            slotProps={{
              htmlInput: { maxLength: 80 }
            }}
          />
        </InputWrap>
        <Footer>
          <PrimaryButton variant="contained" color="primary" disabled={!valid} onClick={goNext}>
            {t('Assessment.infoNext')}
          </PrimaryButton>
        </Footer>
      </>
    );
  }

  if (stepKey === 'email') {
    const trimmed = (values.email || '').trim();
    const valid = EMAIL_RE.test(trimmed);
    const skip = () => {
      setField('email', '');
      // Defer goNext so the setField state update is captured before navigating.
      setTimeout(() => {
        const nextIndex = stepIndex + 1;
        if (nextIndex >= STEPS.length) finish();
        else goToStep(STEPS[nextIndex]);
      }, 0);
    };
    const submitEmailAndContinue = () => {
      // Fire-and-forget the resume-link email; only once per session.
      // Backend silently no-ops on demo / no-voucher sessions.
      const sentKey = 'assessment_resume_email_sent_to';
      const alreadySent = sessionStorage.getItem(sentKey) === trimmed;
      if (!alreadySent) {
        sessionStorage.setItem(sentKey, trimmed);
        try {
          dispatch(
            assessmentEmailResumeLink(trimmed, { name: (values.name || '').trim() })
          ).catch((err) => {
            // Non-blocking — clear the marker so the user can retry by going back.
            console.warn('Resume-link email failed:', err);
            sessionStorage.removeItem(sentKey);
          });
        } catch (err) {
          console.warn('Resume-link email dispatch failed:', err);
          sessionStorage.removeItem(sentKey);
        }
      }
      goNext();
    };
    return (
      <>
        {renderProgress()}
        <Question variant="h1">
          {t('Assessment.infoEmailQuestion', { defaultValue: 'What’s your email?' })}
        </Question>
        <Hint>
          {t('Assessment.infoEmailHint', {
            defaultValue: 'We’ll send your detailed results to this email when you finish the assessment.',
          })}
        </Hint>
        <InputWrap>
          <StyledInput
            fullWidth
            autoFocus
            type="email"
            value={values.email || ''}
            onChange={(e) => setField('email', e.target.value)}
            placeholder={t('Assessment.infoEmailPlaceholder', { defaultValue: 'you@example.com' })}
            onKeyDown={(e) => { if (e.key === 'Enter' && valid) submitEmailAndContinue(); }}
            slotProps={{
              htmlInput: { maxLength: 254, inputMode: 'email', autoComplete: 'email' }
            }}
          />
        </InputWrap>
        <OtherLinkWrap>
          <OtherLink type="button" onClick={skip}>
            {t('Assessment.infoEmailSkip', { defaultValue: 'I don’t have an email' })}
          </OtherLink>
        </OtherLinkWrap>
        <Footer>
          <PrimaryButton variant="contained" color="primary" disabled={!valid} onClick={submitEmailAndContinue}>
            {t('Assessment.infoNext')}
          </PrimaryButton>
        </Footer>
      </>
    );
  }

  if (stepKey === 'age') {
    const ageValid = Number.isInteger(values.age) && values.age >= 7 && values.age <= 99;
    const onAgeChange = (e) => {
      const raw = e.target.value.replace(/[^0-9]/g, '');
      const n = raw === '' ? null : Math.min(99, Math.max(0, parseInt(raw, 10)));
      setField('age', n);
      if (n != null) setField('birthYear', new Date().getFullYear() - n);
    };
    return (
      <>
        {renderProgress()}
        <Question variant="h1">{t('Assessment.infoAgeQuestion')}</Question>
        <Hint>{t('Assessment.infoAgeHint')}</Hint>
        <InputWrap>
          <StyledInput
            fullWidth
            autoFocus
            type="text"
            inputMode="numeric"
            value={values.age ?? ''}
            onChange={onAgeChange}
            placeholder={t('Assessment.infoAgePlaceholder')}
            onKeyDown={(e) => { if (e.key === 'Enter' && ageValid) goNext(); }}
            slotProps={{
              htmlInput: { maxLength: 2 }
            }}
          />
        </InputWrap>
        <Footer>
          <PrimaryButton variant="contained" color="primary" disabled={!ageValid} onClick={goNext}>
            {t('Assessment.infoNext')}
          </PrimaryButton>
        </Footer>
      </>
    );
  }

  if (stepKey === 'gender') {
    const valid = !!values.gender;
    return (
      <>
        {renderProgress()}
        <Question variant="h1">{t('Assessment.infoGenderQuestion')}</Question>
        <GenderCardsRow>
          <GenderCard
            type="button"
            isSelected={values.gender === 'male'}
            onClick={() => setField('gender', 'male')}
          >
            <Mars size={36} strokeWidth={1.75} />
            {t('Assessment.infoGenderMale')}
          </GenderCard>
          <GenderCard
            type="button"
            isSelected={values.gender === 'female'}
            onClick={() => setField('gender', 'female')}
          >
            <Venus size={36} strokeWidth={1.75} />
            {t('Assessment.infoGenderFemale')}
          </GenderCard>
        </GenderCardsRow>
        <OtherLinkWrap>
          <OtherLink
            type="button"
            isSelected={values.gender === 'other'}
            onClick={() => setField('gender', 'other')}
          >
            {t('Assessment.infoGenderOther')}
          </OtherLink>
        </OtherLinkWrap>
        <Footer>
          <PrimaryButton variant="contained" color="primary" disabled={!valid} onClick={goNext}>
            {t('Assessment.infoNext')}
          </PrimaryButton>
        </Footer>
      </>
    );
  }

  if (stepKey === 'certainty') {
    const valid = typeof values.certainty === 'number';
    // Slider works in the same [-2, 2] domain the rest of the assessment uses
    // for certainty answers, so no value translation is needed downstream.
    const sliderValue = valid ? values.certainty : 0;
    return (
      <>
        {renderProgress()}
        <Question variant="h1">{t('Assessment.infoCertaintyQuestion')}</Question>
        <Hint>{t('Assessment.infoCertaintyHint')}</Hint>
        <CertaintySliderWrap>
          <Slider
            value={sliderValue}
            min={-2}
            max={2}
            step={1}
            marks={CERTAINTY_OPTIONS.map((v) => ({ value: v }))}
            onChange={(_, v) => setField('certainty', Array.isArray(v) ? v[0] : v)}
            aria-label={t('Assessment.infoCertaintyQuestion')}
          />
        </CertaintySliderWrap>
        <ScaleEnds>
          <span>{t('Assessment.infoCertaintyScaleLow')}</span>
          <span>{t('Assessment.infoCertaintyScaleHigh')}</span>
        </ScaleEnds>
        <SelectedLabel>
          {valid ? t(`Assessment.infoCertainty${values.certainty}`) : ''}
        </SelectedLabel>
        <Footer>
          <PrimaryButton variant="contained" color="primary" disabled={!valid} onClick={goNext}>
            {t('Assessment.infoNext')}
          </PrimaryButton>
        </Footer>
      </>
    );
  }

  if (stepKey === 'occupations') {
    // Tone the prompt to the user's stated career certainty: high → praise
    // and ask which directions they already have; mid → invite a few options;
    // low → reassure and make the field obviously optional.
    const certainty = typeof values.certainty === 'number' ? values.certainty : 0;
    let bucket;
    if (certainty >= 1) bucket = 'High';
    else if (certainty <= -1) bucket = 'Low';
    else bucket = 'Mid';
    return (
      <>
        {renderProgress()}
        {renderIcon()}
        <Question variant="h1">{t(`Assessment.infoOccupationsHeading${bucket}`)}</Question>
        <Hint>{t(`Assessment.infoOccupationsHint${bucket}`)}</Hint>
        <InputWrap>
          <StyledInput
            fullWidth
            autoFocus
            multiline
            minRows={1}
            maxRows={10}
            value={values.occupationsInMind || ''}
            onChange={(e) => setField('occupationsInMind', e.target.value)}
            placeholder={t('Assessment.infoOccupationsPlaceholder')}
            slotProps={{
              htmlInput: { maxLength: 2000 }
            }}
          />
        </InputWrap>
        <Footer>
          <PrimaryButton variant="contained" color="primary" onClick={finish}>
            {t('Assessment.infoFinish')}
          </PrimaryButton>
        </Footer>
      </>
    );
  }

  return null;
}
